import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Calendar as CalendarIcon } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import {
  formatIsoDateDisplay,
  isoDateToLocalDate,
  localDateToIsoDate,
  parseDisplayDateToIso,
  todayIsoDate,
} from '@/lib/date'
import { resolveDatePickerLocale } from './datePickerLocale'

export interface DatePickerProps {
  id?: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  error?: boolean
  min?: string
  max?: string
  variant?: 'default' | 'field'
  allowManualInput?: boolean
  containerClassName?: string
  leadingContent?: ReactNode
  trailingContent?: ReactNode
  errorPlacement?: 'bottom' | 'right'
  openOnFieldClick?: boolean
}

export function DatePicker({
  id,
  value,
  onChange,
  placeholder,
  disabled,
  className,
  error,
  min,
  max,
  variant = 'default',
  allowManualInput = true,
  containerClassName,
  leadingContent,
  trailingContent,
  errorPlacement = 'bottom',
  openOnFieldClick = false,
}: DatePickerProps) {
  const { t, i18n } = useTranslation('shared')
  const calendarLocale = resolveDatePickerLocale(
    i18n.resolvedLanguage ?? i18n.language
  )
  const resolvedPlaceholder = placeholder ?? t('datePicker.placeholder')
  const anchorRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = useState(() =>
    value ? formatIsoDateDisplay(value) : ''
  )
  const [inputError, setInputError] = useState<string | null>(null)
  const [visibleMonth, setVisibleMonth] = useState<Date>(
    () => isoDateToLocalDate(value) ?? new Date()
  )

  const selected = isoDateToLocalDate(value)

  const isDateDisabled = (date: Date) => {
    const iso = localDateToIsoDate(date)
    if (min && iso < min) return true
    if (max && iso > max) return true
    return false
  }

  const wrapperClass =
    variant === 'field'
      ? 'flex h-10 w-full items-center gap-2 rounded-lg border border-input bg-background px-4 py-3 text-base focus-within:outline-none disabled:cursor-not-allowed disabled:opacity-50'
      : 'flex h-10 w-full items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm focus-within:outline-none disabled:cursor-not-allowed disabled:opacity-50'

  const inputClass =
    variant === 'field'
      ? 'min-w-0 flex-1 border-0 bg-transparent p-0 text-sm font-normal placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50'
      : 'min-w-0 flex-1 border-0 bg-transparent p-0 text-sm font-normal placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50'

  const [syncedValue, setSyncedValue] = useState(value)
  if (syncedValue !== value) {
    setSyncedValue(value)
    setInputValue(value ? formatIsoDateDisplay(value) : '')
    setInputError(null)
  }

  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) setVisibleMonth(isoDateToLocalDate(value) ?? new Date())
  }

  const openCalendar = () => {
    if (!disabled) setOpen(true)
  }

  const selectDate = (iso: string, close = true) => {
    onChange(iso)
    setInputValue(iso ? formatIsoDateDisplay(iso) : '')
    setInputError(null)
    if (close) setOpen(false)
  }

  const commitInput = () => {
    if (!allowManualInput) return

    if (inputValue.trim() === (value ? formatIsoDateDisplay(value) : '')) {
      return
    }

    if (!inputValue.trim()) {
      selectDate('', false)
      return
    }

    const parsed = parseDisplayDateToIso(inputValue)
    if (parsed === null) {
      setInputValue(value ? formatIsoDateDisplay(value) : '')
      setInputError(t('datePicker.invalidDate'))
      return
    }

    if (parsed && min && parsed < min) {
      setInputValue(value ? formatIsoDateDisplay(value) : '')
      setInputError(t('datePicker.invalidDate'))
      return
    }

    if (parsed && max && parsed > max) {
      setInputValue(value ? formatIsoDateDisplay(value) : '')
      setInputError(t('datePicker.invalidDate'))
      return
    }

    selectDate(parsed, false)
  }

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      commitInput()
      setOpen(false)
      return
    }
    if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && !open) {
      event.preventDefault()
      openCalendar()
    }
  }

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) commitInput()
  }

  const todayDate = isoDateToLocalDate(todayIsoDate())
  const todayDisabled = !todayDate || isDateDisabled(todayDate)

  const handleToday = () => {
    if (!todayDate || todayDisabled) return
    setVisibleMonth(todayDate)
    selectDate(todayIsoDate())
  }

  const showError = error || Boolean(inputError)
  const showErrorOnRight = errorPlacement === 'right'

  return (
    <div
      className={cn(
        'w-full',
        inputError &&
          (showErrorOnRight ? 'flex items-center gap-2' : 'space-y-1'),
        containerClassName
      )}
    >
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverAnchor asChild>
          <div
            ref={anchorRef}
            data-has-value={inputValue ? 'true' : 'false'}
            onClick={openOnFieldClick ? openCalendar : undefined}
            className={cn(
              wrapperClass,
              className,
              showError && 'border-destructive',
              disabled && 'opacity-50'
            )}
          >
            {leadingContent}
            <PopoverTrigger asChild>
              <button
                type='button'
                disabled={disabled}
                aria-label={t('datePicker.openCalendar')}
                className='shrink-0 text-foreground disabled:cursor-not-allowed'
              >
                <CalendarIcon className='h-4 w-4' aria-hidden='true' />
              </button>
            </PopoverTrigger>
            <input
              id={id}
              type='text'
              inputMode='numeric'
              autoComplete='off'
              autoCorrect='off'
              spellCheck={false}
              data-1p-ignore
              data-lpignore='true'
              disabled={disabled}
              readOnly={!allowManualInput}
              value={inputValue}
              placeholder={resolvedPlaceholder}
              aria-invalid={showError || undefined}
              onChange={event => {
                setInputValue(event.target.value)
                setInputError(null)
              }}
              onBlur={() => {
                if (open) return
                commitInput()
              }}
              onKeyDown={handleInputKeyDown}
              onClick={openCalendar}
              className={inputClass}
            />
            {trailingContent}
          </div>
        </PopoverAnchor>
        <PopoverContent
          align='start'
          side='bottom'
          sideOffset={4}
          collisionPadding={8}
          className='pointer-events-auto w-auto p-0'
          onOpenAutoFocus={event => event.preventDefault()}
          onPointerDownOutside={event => {
            if (anchorRef.current?.contains(event.target as Node)) {
              event.preventDefault()
            }
          }}
          onInteractOutside={event => {
            if (anchorRef.current?.contains(event.target as Node)) {
              event.preventDefault()
            }
          }}
        >
          <Calendar
            mode='single'
            required
            captionLayout='dropdown'
            locale={calendarLocale}
            weekStartsOn={1}
            className='p-2 text-sm [&_.rdp-button\_next]:size-6 [&_.rdp-button\_previous]:size-6 [&_.rdp-caption\_label]:h-6 [&_.rdp-caption\_label]:text-sm [&_.rdp-day\_button]:size-[1.4rem] [&_.rdp-day\_button]:min-w-0 [&_.rdp-dropdowns]:h-6 [&_.rdp-month]:gap-2 [&_.rdp-month\_caption]:h-6 [&_.rdp-week]:mt-1 [&_.rdp-weekday]:text-[0.7rem]'
            fixedWeeks
            month={visibleMonth}
            onMonthChange={setVisibleMonth}
            selected={selected}
            startMonth={new Date(1900, 0)}
            endMonth={new Date(2100, 11)}
            disabled={isDateDisabled}
            onSelect={date => {
              selectDate(localDateToIsoDate(date))
            }}
          />
          <div className='border-t border-border p-1.5'>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='h-6 w-full justify-center text-sm font-normal'
              disabled={todayDisabled}
              onClick={handleToday}
            >
              {t('datePicker.today')}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      {inputError && (
        <p
          className={cn(
            'text-xs text-destructive',
            showErrorOnRight && 'shrink-0 whitespace-nowrap'
          )}
        >
          {inputError}
        </p>
      )}
    </div>
  )
}
